import { useEffect, useMemo, useState } from "react";
import { emojiDataIfLoaded, loadEmojiData, searchEmoji } from "../lib/emoji-data.js";
import type { EmojiData } from "../lib/emoji-data.js";
import "./mention-autocomplete.css";

/**
 * `:shortcode` completion in the composer — `:tada` offers 🎉.
 *
 * Shares the mention picker's look and keyboard contract (↑/↓, Enter or Tab
 * picks, Escape dismisses) so the two read as one feature. Opens from two
 * characters, so a time like "10:30" or a smiley ":)" does not pop a list.
 */

export function activeShortcodeQuery(value: string, caret: number): { query: string; start: number } | null {
  const before = value.slice(0, caret);
  const match = /(^|\s):([a-z0-9_+-]{2,30})$/i.exec(before);
  if (!match) return null;
  return { query: match[2]!.toLowerCase(), start: before.length - match[2]!.length - 1 };
}

export interface EmojiAutocompleteProps {
  value: string;
  caret: number;
  onPick: (nextValue: string, nextCaret: number) => void;
  onOpenChange?: (open: boolean) => void;
}

export function EmojiAutocomplete({ value, caret, onPick, onOpenChange }: EmojiAutocompleteProps) {
  const [data, setData] = useState<EmojiData | null>(emojiDataIfLoaded());
  const [index, setIndex] = useState(0);
  const [dismissedAt, setDismissedAt] = useState<number | null>(null);
  const active = activeShortcodeQuery(value, caret);

  // The dataset is fetched the first time somebody types a shortcode.
  useEffect(() => {
    if (!active || data) return;
    let cancelled = false;
    void loadEmojiData().then((loaded) => {
      if (!cancelled) setData(loaded);
    });
    return () => {
      cancelled = true;
    };
  }, [active, data]);

  const matches = useMemo(() => (active && data ? searchEmoji(data, active.query, 6) : []), [active, data]);
  const open = active !== null && matches.length > 0 && dismissedAt !== active.start;

  useEffect(() => onOpenChange?.(open), [open, onOpenChange]);
  useEffect(() => setIndex(0), [active?.query]);

  const choose = (unicode: string) => {
    if (!active) return;
    const next = value.slice(0, active.start) + unicode + value.slice(caret);
    onPick(next, active.start + unicode.length);
  };

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setIndex((prev) => (prev + 1) % matches.length);
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setIndex((prev) => (prev - 1 + matches.length) % matches.length);
      } else if (event.key === "Enter" || event.key === "Tab") {
        event.preventDefault();
        const picked = matches[index];
        if (picked) choose(picked.unicode);
      } else if (event.key === "Escape") {
        event.preventDefault();
        if (active) setDismissedAt(active.start);
      }
    };
    // Capture: the composer's own Enter handler must not fire first.
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  });

  if (!open) return null;

  return (
    <div className="mention-ac" role="listbox" aria-label="Emoji">
      {matches.map((emoji, i) => (
        <button
          key={emoji.unicode}
          type="button"
          role="option"
          aria-selected={i === index}
          className={`mention-ac__row ${i === index ? "is-active" : ""}`}
          onMouseDown={(event) => {
            event.preventDefault();
            choose(emoji.unicode);
          }}
          onMouseEnter={() => setIndex(i)}
        >
          <span className="mention-ac__emoji" aria-hidden="true">
            {emoji.unicode}
          </span>
          <span className="mention-ac__name">:{emoji.shortcodes[0] ?? emoji.label}:</span>
        </button>
      ))}
    </div>
  );
}
